import {Asset} from '@deviceinsight/ng-ui-api-client';
import {AssetWithMetaData, StateAccess} from '../types';
import {
	DATAPOINTS_BY_TYPE,
	DISPLAY_ELEMENT_CLASSNAME,
	ELEMENT_CLASSNAME,
	INTERACTIVE_ELEMENT_CLASSNAME,
	SVG_STRIP_REGEXP
} from '../utils/consts';
import {getAssetDeviceType} from '../../../ruleengine/ruleEditHelpers';
import {SBOX_TYPE} from '../../../../modules/common/consts';
import {WidgetSvg} from '../hooks/types';
import {DeviceType} from '../../../../modules/ruleengine/config/consts';
import {getSvgByDeviceType} from '../../common/utils';

const getDataPointsForDeviceType = (deviceType: string) => {
	return DATAPOINTS_BY_TYPE[deviceType] || [];
};

const hideAllMarkersExceptNode = (svgRootObj: WidgetSvg | undefined, exceptNode: string | null | undefined) => {
	if (svgRootObj) {
		const elementsToToggle = svgRootObj.select(`g.${ELEMENT_CLASSNAME}`);

		if (elementsToToggle && elementsToToggle.members) {
			elementsToToggle.members.forEach((element) => {
				const marker = element.select(`rect#marker`)?.members[0];
				if (marker) {
					marker.node.style.visibility = exceptNode && exceptNode === element.node ? 'visible' : 'hidden';
				}
			});
		}
	}
};

const setAlertMarkers = (svgRootObj: WidgetSvg | undefined, alertingIds: Array<number>) => {
	if (svgRootObj) {
		const elementsToToggle = svgRootObj.select(`g.${ELEMENT_CLASSNAME}`);

		if (elementsToToggle && elementsToToggle.members) {
			elementsToToggle.members.forEach((element) => {
				const assetId = element.attr('id').replace('added_device_', '');
				const isAlerting = alertingIds.find((alertingId) => parseInt(assetId) === alertingId);
				const alertElement = element.select(`g#alert`);

				if (alertElement && alertElement.members.length) {
					const alertGroup = alertElement.members[0];
					alertGroup.node.style.visibility = isAlerting ? 'visible' : 'hidden';
				}
			});
		}
	}
};

const markMissingAssets = (svgRootObj: WidgetSvg, assetsInGroup: Array<Asset>) => {
	const assetIdsNotInGroup: number[] = [];
	const allDeviceElementsInSvg = svgRootObj.select(`g.${ELEMENT_CLASSNAME}`);
	allDeviceElementsInSvg?.members.forEach((member) => {
		const assetId = parseInt(member.attr('id').replace('added_device_', ''));
		const isAssetLoaded = assetsInGroup.find((asset) => asset.id === assetId);

		if (!isAssetLoaded) {
			assetIdsNotInGroup.push(assetId);
		}
	});
	setAlertMarkers(svgRootObj, assetIdsNotInGroup);
};

const addSvgDeviceHandlers = (
	svgDeviceObj: WidgetSvg,
	svgRootObj: WidgetSvg,
	device: AssetWithMetaData | undefined,
	updateSvg: () => void,
	setSelectedDevice: (arg0: AssetWithMetaData | undefined) => void
): void => {
	svgDeviceObj.draggable({
		snapToGrid: 10
	});
	svgDeviceObj.on('dragend', () => updateSvg());
	svgDeviceObj.on('click', (e: Event) => {
		e.stopImmediatePropagation();
		hideAllMarkersExceptNode(svgRootObj, svgDeviceObj.members[0].node);
		setSelectedDevice(device);
	});
};

const addUnknownDeviceHandlers = (
	svgDeviceObj: WidgetSvg,
	svgRootObj: WidgetSvg,
	updateSvg: () => void,
	setSelectedDevice: (arg0: AssetWithMetaData | undefined) => void
): void => {
	svgDeviceObj.draggable({
		snapToGrid: 10
	});
	svgDeviceObj.on('dragend', () => updateSvg());
	svgDeviceObj.on('click', (e: Event) => {
		e.stopImmediatePropagation();
		hideAllMarkersExceptNode(svgRootObj, svgDeviceObj.members[0].node);
		const id = svgDeviceObj.members[0].id().replace('added_device_', '');
		setSelectedDevice({
			asset: {
				id: parseInt(id),
				name: 'Unknown',
				urn: '',
				ancestorAssetGroupIds: [],
				assetGroupIds: [],
				assetDefinitionId: null,
				gatewayId: undefined,
				isGateway: true,
				properties: {}
			},
			meta: {
				type: 'unknown'
			}
		});
	});
};

const onClickAnyWhere = (
	event: MouseEvent | TouchEvent,
	setSelectedDevice: (arg0: AssetWithMetaData | undefined) => void,
	svgRootObj: WidgetSvg | undefined
) => {
	if (svgRootObj) {
		setSelectedDevice(undefined);
		hideAllMarkersExceptNode(svgRootObj, undefined);
	}
};

type SVGDimensions = {
	width: number;
	height: number;
};

const getSvgDimensions = (svgRootObj: WidgetSvg): SVGDimensions => {
	function parse(value: string | number) {
		if (typeof value === 'string') {
			return parseInt(value.replace('px', ''));
		}
		return value;
	}
	return {
		width: parse(svgRootObj.width()) || parse(svgRootObj.viewbox().width),
		height: parse(svgRootObj.height()) || parse(svgRootObj.viewbox().height)
	};
};

const addDevicesToSvg = (
	devicesToAdd: Array<AssetWithMetaData>,
	updateSvg: () => void,
	stateAccessParams: StateAccess
) => {
	const {svgRootObj, setSelectedDevice} = stateAccessParams;

	if (svgRootObj) {
		let yOffsetFactor = 0;
		let xPos = 0;
		// get the scale of the already added assets
		let targetScale = 0;
		const svgDevices = svgRootObj.select(`.added-element`);

		if (svgDevices && svgDevices.members?.length > 0) {
			targetScale = svgDevices.members[0].transform().scaleX ?? 0;
		}

		devicesToAdd.forEach((device, index) => {
			const {
				asset: {id, name, urn},
				meta: {type}
			} = device;
			const svgCode = getSvgByDeviceType(type as DeviceType);

			if (svgCode) {
				const res = svgCode.match(SVG_STRIP_REGEXP);

				if (res && res.length === 4) {
					// res[0] contains full match, res[1] the opening svg tag, res[2] the content and res [3] the closing svg tag
					const svgContents = res[2];
					// now we got the content and group it....
					const svgId = `added_device_${id}`;
					const className = type === SBOX_TYPE ? INTERACTIVE_ELEMENT_CLASSNAME : DISPLAY_ELEMENT_CLASSNAME;
					const title = `${name} - urn: ${urn}`;
					const groupedInner =
						`<g id='${svgId}' class='${ELEMENT_CLASSNAME} ${className}'><title>${title}</title>` +
						svgContents +
						'</g>';
					svgRootObj.svg(groupedInner);
					const svgDeviceObj = svgRootObj.select(`g#${svgId}`);
					const {width, height} = getSvgDimensions(svgRootObj);

					// The  assets are 10% of the floorplan width if scale is 100 - in case no asset has been added / scaled yet we calculat ethe initial scale
					// otherwise we use the scale of one of the already added assets.
					if (width && width > 0 && targetScale === 0) {
						targetScale = (width / 50) * 0.01;
					}

					// create an y offset if the row is full....
					let cy = 0;

					if (xPos < width - width / 10) {
						xPos = (width / 10) * index;
					} else {
						xPos = 0;
						yOffsetFactor++;
						cy = yOffsetFactor * (height / 4);
					}

					svgDeviceObj?.transform({
						scale: targetScale,
						cx: xPos,
						cy
					});
					addSvgDeviceHandlers(svgDeviceObj as WidgetSvg, svgRootObj, device, updateSvg, setSelectedDevice);
				}
			}
		});
	}
};

const removeDeviceFromFloorplan = (updateSvg: () => void, stateAccessParams: StateAccess) => {
	const {svgRootObj, availableDevices, setAvailableDevices, selectedDevice, setSelectedDevice} = stateAccessParams;

	if (svgRootObj && selectedDevice) {
		const {
			asset: {id}
		} = selectedDevice;
		const svgId = `added_device_${id}`;
		const svgDeviceObj = svgRootObj.select(`g#${svgId}`);
		const modifiedDevices = availableDevices.filter((device) => device.asset.id !== id);
		setAvailableDevices(modifiedDevices);
		setSelectedDevice(undefined);
		if (svgDeviceObj && svgDeviceObj.members && svgDeviceObj.members.length) svgDeviceObj.members[0].remove();
		updateSvg();
	}
};

const removeAllDevicesFromFloorplan = (updateSvg: () => void, stateAccessParams: StateAccess) => {
	const {setAvailableDevices, setSelectedDevice, svgRootObj} = stateAccessParams;

	if (svgRootObj) {
		const allDeviceElementsInSvg = svgRootObj.select(`g.${ELEMENT_CLASSNAME}`);
		allDeviceElementsInSvg?.members.forEach((member) => {
			member.remove();
		});
		setAvailableDevices([]);
		setSelectedDevice(undefined);
		updateSvg();
	}
};

const onAddAssets = (assets: Array<Asset>, updateSvg: () => void, stateAccessParams: StateAccess) => {
	const {setAvailableDevices, setIsSearching, availableDevices} = stateAccessParams;
	// we replace the complete set of availableDevices with the assets delivered here
	// thus we check if a device is added already, we use that state
	const addedDevices = assets.reduce((devices: Array<AssetWithMetaData>, asset: Asset) => {
		const deviceType: string = getAssetDeviceType(asset);

		if (deviceType === DeviceType.sLight || deviceType === DeviceType.sBox || deviceType === DeviceType.kisIo) {
			devices.push({
				asset: asset,
				meta: {
					type: deviceType
				}
			});
		}

		return devices;
	}, []);
	addDevicesToSvg(addedDevices, updateSvg, stateAccessParams);
	setAvailableDevices([...availableDevices, ...addedDevices]);
	setIsSearching(false);
	updateSvg();
};

const updateAssetScaling = (svgRootObj: WidgetSvg | undefined, updateSvg: () => void, scaleUp: boolean) => {
	if (svgRootObj) {
		const svgDevices = svgRootObj.select(`.added-element`);
		const factor = scaleUp ? 1.1 : 0.9;

		if (svgDevices && svgDevices.members.length > 0) {
			svgDevices.members.forEach((member) => {
				const currentTransform = member.transform();
				const currentScale = currentTransform.scaleX ?? 1;
				const newScale = currentScale * factor;
				member.transform({
					scale: newScale
				});
			});
			updateSvg();
		}
	}
};

export {
	onAddAssets,
	removeAllDevicesFromFloorplan,
	removeDeviceFromFloorplan,
	addDevicesToSvg,
	addSvgDeviceHandlers,
	addUnknownDeviceHandlers,
	hideAllMarkersExceptNode,
	setAlertMarkers,
	markMissingAssets,
	onClickAnyWhere,
	getDataPointsForDeviceType,
	updateAssetScaling
};
